/**
 * CallCenter V2 – shared API helpers
 * All CCV2 services import from here to stay DRY.
 */
import accessLocalStorage from '../accessLocalStorage';

/** @returns {Promise<string>} base URL like http://host (without port) */
export const getCcv2ApiBase = async () => {
  const envBase = process.env.REACT_APP_CALLCENTER_V2_BASE_URL;
  if (envBase) return envBase;
  const { ipRequest } = accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

/** @returns {string} tenant ID */
export const getCcv2TenantId = () => {
  const { user: { idtenant } } = accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

/** @returns {string} Bearer token */
export const getCcv2Token = () => {
  const { token } = accessLocalStorage.getUserLocalStorage();
  return token;
};

/**
 * Returns current user info including role decoded from JWT payload.
 * @returns {{ userId: number|null, role: string, agentKey: string|null, idtenant: string, username: string }}
 */
export const getCcv2CurrentUser = () => {
  const stored = accessLocalStorage.getUserLocalStorage();
  const user = stored?.user || {};
  const token = stored?.token;

  let jwtRole = 'user';
  let jwtAgentKey = null;
  let jwtUserId = null;

  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      // Payload might have nested data object (as in backend auth.guard)
      const data = payload?.data || payload;
      jwtRole = data?.role ?? payload?.role ?? 'user';
      jwtAgentKey = data?.agentKey ?? payload?.agentKey ?? null;
      jwtUserId = data?.userId ?? payload?.userId ?? payload?.sub ?? null;
    } catch (_e) {
      // JWT decode failed — keep defaults
    }
  }

  // Fallback to stored user role if JWT decode fails
  const role = jwtRole || user.role || user.user_role || 'user';

  return {
    userId: jwtUserId,
    role,
    agentKey: jwtAgentKey,
    idtenant: String(user.idtenant || ''),
    username: user.username || user.name || '',
    isManager: role === 'manager' || role === 'admin' || role === 'root',
    isRoot: role === 'root',
  };
};

/**
 * Feature flag: ccv2_phase5_ui
 * Default ON only for root/vgs, otherwise respects localStorage.
 */
export const isPhase5Enabled = () => {
  const stored = localStorage.getItem('ccv2_phase5_ui');
  if (stored !== null) {
    return stored === 'true';
  }
  // Auto-enable for root users on tenant vgs
  try {
    const u = getCcv2CurrentUser();
    return u.isRoot || u.idtenant === 'vgs' || u.idtenant === '1';
  } catch (_e) {
    return false;
  }
};

/**
 * Unified fetch wrapper for CCV2 API.
 * @param {string} path - e.g. '/v2/callcenter/agents'
 * @param {RequestInit} [options]
 * @returns {Promise<any>}
 */
export const ccv2Fetch = async (path, options = {}) => {
  const base = await getCcv2ApiBase();
  const token = getCcv2Token();
  const tenantId = getCcv2TenantId();

  // Nginx proxies /v2/callcenter/* to localhost:4010 — go through the
  // standard 443 port so external clients (production HTTPS) work too.
  const sep = path.includes('?') ? '&' : '?';
  const url = `${base}${path}${sep}tenant=${tenantId}`;

  const res = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenantId,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ message: res.statusText }));
    const err = new Error(errBody.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = errBody;
    throw err;
  }

  if (res.status === 204) return null;
  return res.json();
};
